import { useAccount, useWalletClient } from 'wagmi';
import { SiweMessage } from 'siwe';
import { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { ProviderType } from '@lit-protocol/constants';
import { LitAbility, LitPKPResource, LitActionResource, RecapSessionCapabilityObject } from '@lit-protocol/auth-helpers';
import { useLocalStorage } from 'usehooks-ts';
import { useSession } from '@/contexts/LitSessionProvider';
import signMessageWithActionWrapper from '@/lib/executorHelpers/signMessageWithActionWrapper';
export default function useWalletAuthSig() {
  const {
    litNetwork
  } = useSession();
  const {
    address: account
  } = useAccount();
  const {
    data: walletClient
  } = useWalletClient();
  const [authSig, setAuthSig] = useLocalStorage('authsig', null);
  const [origin, setOrigin] = useState(null);
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);
  const message = `Signing this message will authenticate you as the owner of the Lit Cloud Wallet tied to your EVM address at ${account}. Timestamp: ${new Date().toISOString().slice(0, 19).replace('T', ' ')} on ${origin}`;
  const sign = async ({
    nonce,
    pkp,
    useLitAction = false,
    authMethod,
    sessionLength,
    litActionType = ProviderType.Google
  }) => {
    var _a;
    try {
      let authSig;
      let recoveredAddress;
      let siweMessage = new SiweMessage({
        domain: 'localhost:3000',
        address: account,
        uri: 'localhost:3000',
        statement: message,
        version: '1',
        chainId: 1,
        nonce,
        expirationTime: new Date(Date.now() + sessionLength * 1000).toISOString()
      });
      let signature;
      console.log('SIWE message', siweMessage);
      if (pkp) {
        console.log('Adding pkp signing capability to siwe message');
        const sessionCapabilityObject = new RecapSessionCapabilityObject({}, []);
        const litPkpResource = new LitPKPResource(pkp.publicKey);
        sessionCapabilityObject.addCapabilityForResource(litPkpResource, LitAbility.PKPSigning);
        sessionCapabilityObject.addCapabilityForResource(new LitActionResource(litActionType), LitAbility.LitActionExecution);
        siweMessage = sessionCapabilityObject.addToSiweMessage(siweMessage);
        console.log('SIWE message with pkp signing capability', siweMessage);
      }
      const messageToSign = siweMessage.prepareMessage();
      console.log('Message to sign to create wallet authsig', messageToSign);
      const hexlifiedMessage = ethers.utils.arrayify(ethers.utils.keccak256(ethers.utils.toUtf8Bytes(JSON.stringify(messageToSign))));
      // Added in if Google Auth fails, and we need to use the Google Auth Lit Action
      if (useLitAction) {
        console.log('Signing message with lit action');
        const response = await signMessageWithActionWrapper({
          type: litActionType,
          authMethods: [authMethod],
          pkp,
          network: litNetwork,
          environment: 'DEV',
          message: hexlifiedMessage
        });
        signature = response === null || response === void 0 ? void 0 : response.signature;
        console.log('Signature', signature);
        const recoveredAddress = ethers.utils.verifyMessage(hexlifiedMessage, signature);
        console.log('Recovered address', recoveredAddress, hexlifiedMessage);
        authSig = {
          sig: signature,
          derivedVia: 'web3.eth.personal.sign',
          signedMessage: messageToSign,
          address: pkp === null || pkp === void 0 ? void 0 : pkp.ethAddress
        };
      } else {
        signature = await walletClient.signMessage({
          account,
          message: messageToSign
        });
        recoveredAddress = (_a = ethers === null || ethers === void 0 ? void 0 : ethers.utils) === null || _a === void 0 ? void 0 : _a.verifyMessage(messageToSign, signature);
        authSig = {
          sig: signature,
          derivedVia: 'web3.eth.personal.sign',
          signedMessage: messageToSign,
          address: recoveredAddress
        };
      }
      setAuthSig(authSig);
      console.log({
        authSig,
        signature,
        recoveredAddress
      });
      return {
        authSig,
        signature,
        recoveredAddress,
        messageToSign
      };
    } catch (e) {
      console.error('[useWalletAuthSig/sign]', e);
      return {
        error: 'Error signing message'
      };
    }
  };
  return {
    authSig,
    sign
  };
}